"use client"

import { useEffect } from "react"

import { settleDeepLinkDecision } from "@/lib/deep-link-gate"

// `App.getLaunchUrl()` keeps returning the original launch URL on every remount
// and full reload, so we record which deep-link URL we've already acted on and
// skip it thereafter — otherwise we'd re-navigate to an already-consumed
// single-use magic-link token and bounce the user back to sign-in.
const HANDLED_KEY = "st_deeplink_handled"
// A cold start can hand over the launch URL twice: once from `getLaunchUrl()`
// and again as a retained `appUrlOpen`, possibly after the reload the first one
// caused. Inside this window an identical `appUrlOpen` is that echo, not a tap.
const HANDLED_AT_KEY = "st_deeplink_handled_at"
const ECHO_WINDOW_MS = 10_000

/**
 * Is this URL one we have already acted on, rather than a new tap?
 *
 * - `launch` (`getLaunchUrl()`): replays for the whole session, so the same URL
 *   is only ever handled once.
 * - `open` (`appUrlOpen`): a repeat is a replay only inside the echo window;
 *   after that, the same URL again is the user tapping the same link again.
 */
export function isDeepLinkReplay(input: {
    rawUrl: string
    source: "launch" | "open"
    handledUrl: string | null
    handledAt: number
    now: number
}): boolean {
    if (input.handledUrl !== input.rawUrl) return false
    if (input.source === "launch") return true
    return input.now - input.handledAt < ECHO_WINDOW_MS
}

/**
 * When the native iOS app is opened via a Universal Link (e.g. tapping a
 * magic-link sign-in email), iOS hands the URL to Capacitor but the in-app
 * WebView does NOT navigate on its own. This listener navigates the WebView to
 * the tapped path so sign-in completes inside the app: because the WebView
 * origin is already surveythanks.com, hitting `/api/auth/magic-link/verify`
 * sets the better-auth session cookie in the app's cookie jar and then
 * redirects to `/email-entry/complete`.
 *
 * Universal Links are scoped server-side by the App Site Association file
 * (`app/.well-known/apple-app-site-association/route.ts`), so only the sign-in
 * paths reach here. We still guard on host as defence in depth. Web builds are
 * unaffected — everything is behind `Capacitor.isNativePlatform()` and dynamic
 * imports, mirroring `components/FormIframeKeyboardBridge.tsx`.
 */
export function DeepLinkListener() {
    useEffect(() => {
        let cancelled = false
        let remove: (() => void) | undefined

        const navigateTo = (rawUrl: string | null | undefined, source: "launch" | "open"): boolean => {
            if (!rawUrl || cancelled) return false
            try {
                const url = new URL(rawUrl)
                // Accept both the apex and the canonical www host — magic links
                // are issued against www.surveythanks.com (apex 307-redirects to
                // it). Navigation below uses the path only, so it stays on the
                // WebView's current origin regardless.
                if (url.host !== "surveythanks.com" && url.host !== "www.surveythanks.com") return false
                // Act on a given LAUNCH URL only once per session (see HANDLED_KEY).
                // A warm `appUrlOpen` is a fresh tap, so beyond the cold-start echo
                // window it is never deduped: every gated page bounces through the same
                // sign-in URL, and deduping it made the second tap onwards a
                // no-op — the app just came forward wherever it had been left.
                if (
                    isDeepLinkReplay({
                        rawUrl,
                        source,
                        handledUrl: sessionStorage.getItem(HANDLED_KEY),
                        handledAt: Number(sessionStorage.getItem(HANDLED_AT_KEY) ?? 0),
                        now: Date.now(),
                    })
                ) {
                    return false
                }
                const target = url.pathname + url.search
                // Already on the target path (cold-start may load it anyway).
                if (target === window.location.pathname + window.location.search) return false
                sessionStorage.setItem(HANDLED_KEY, rawUrl)
                sessionStorage.setItem(HANDLED_AT_KEY, String(Date.now()))
                // Tell anything about to route on its own (/resume) to stand
                // down BEFORE the navigation starts — see lib/deep-link-gate.ts.
                settleDeepLinkDecision(true)
                // WebKit cancels this document's in-flight chunk loads as soon as
                // the navigation starts; BootRecorder must not read those as a
                // stale build and reload over the top of it.
                ;(window as unknown as { __stMarkLeaving?: () => void }).__stMarkLeaving?.()
                window.location.href = target
                return true
            } catch {
                // malformed URL — ignore
                return false
            }
        }

        ;(async () => {
            try {
                const { Capacitor } = await import("@capacitor/core")
                if (!Capacitor.isNativePlatform()) {
                    settleDeepLinkDecision(false)
                    return
                }
                const { App } = await import("@capacitor/app")

                // Cold start: the app was launched by the link.
                const launch = await App.getLaunchUrl()
                // A cancelled run (unmounted mid-flight) skipped the navigation
                // only BECAUSE it was cancelled — it has no verdict to give, and
                // saying "nothing to do" here would release /resume moments
                // before the live run navigates. No-op once settled `true`.
                navigateTo(launch?.url, "launch")
                if (!cancelled) settleDeepLinkDecision(false)

                // Warm: the app was already running when the link was tapped.
                const handle = await App.addListener("appUrlOpen", (event) => {
                    navigateTo(event.url, "open")
                })
                if (cancelled) {
                    handle.remove()
                } else {
                    remove = () => handle.remove()
                }
            } catch {
                // @capacitor/app unavailable / not native — no-op on web.
                settleDeepLinkDecision(false)
            }
        })()

        return () => {
            cancelled = true
            remove?.()
        }
    }, [])

    return null
}
